import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

const ACCOUNT_PERKS = [
  'Your scores saved to the global leaderboard',
  'A personal record of every game you play',
  'Send and accept challenges with friends'
];

function AuthCard({ guestOpen, onGuestOpen }) {
  const { login, register, startGuest } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [guestName, setGuestName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const guestInputRef = useRef(null);

  useEffect(() => {
    if (guestOpen && guestInputRef.current) {
      guestInputRef.current.focus({ preventScroll: true });
    }
  }, [guestOpen]);

  async function handleAccountSubmit(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'register') {
        await register({ email, username, password });
      } else {
        await login(emailOrUsername, password);
      }
      navigate('/lobby');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleGuestStart(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await startGuest(guestName.trim());
      navigate('/lobby');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-card surface-panel">
      <aside className="auth-brand">
        <div className="auth-brand-content">
          <span className="auth-eyebrow">Take a seat</span>
          <h2 className="auth-title auth-title-sm">Ready to play?</h2>
          <div className="ornament" aria-hidden="true">
            ✦
          </div>
          <p className="auth-tagline">
            Jump in as a guest in seconds, or create a free account to keep your record and play with friends.
          </p>
          <ul className="auth-feature-list">
            {ACCOUNT_PERKS.map((perk) => (
              <li key={perk}>{perk}</li>
            ))}
          </ul>
        </div>
      </aside>

      <section className="auth-form-panel">
        <div className="auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
            onClick={() => setMode('login')}
          >
            Sign In
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={`auth-tab ${mode === 'register' ? 'active' : ''}`}
            onClick={() => setMode('register')}
          >
            Create Account
          </button>
        </div>

        {error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        )}

        <Form onSubmit={handleAccountSubmit} className="auth-form">
          {mode === 'register' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  placeholder="Choose a display name"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </Form.Group>
            </>
          )}
          {mode === 'login' && (
            <Form.Group className="mb-3">
              <Form.Label>Email or Username</Form.Label>
              <Form.Control
                placeholder="Enter your email or username"
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                required
              />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Form.Group>
          <Button type="submit" className="w-100 auth-submit" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'register' ? 'Create Account' : 'Sign In'}
          </Button>
        </Form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        {!guestOpen ? (
          <Button variant="outline-secondary" className="w-100" onClick={onGuestOpen}>
            Continue as Guest
          </Button>
        ) : (
          <Form onSubmit={handleGuestStart} className="auth-guest-form">
            <Form.Group className="mb-2">
              <Form.Label>Guest display name</Form.Label>
              <Form.Control
                ref={guestInputRef}
                placeholder="What should we call you?"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                required
              />
            </Form.Group>
            <Button type="submit" variant="success" className="w-100" disabled={busy}>
              Play as Guest
            </Button>
          </Form>
        )}

        <p className="auth-fineprint muted-text">
          Guests can play right away. Create an account to save scores and challenge friends.
        </p>
      </section>
    </div>
  );
}

export default AuthCard;
